/**
 * Deterministic Hebrew/number formatting for the narrative and the urgency chips (REDESIGN §3
 * "Rules", §8 "Every number carries its unit").
 *
 * Nothing here calls `Intl` — grouping and rounding are hand-rolled so a golden test asserts the
 * same string on every machine and in every ICU build. Nothing here reads a clock: every function
 * is a pure function of its arguments.
 */
import type { DayString } from './types.ts';

/** U+2212 MINUS SIGN — the same minus `personal/server/util/format.ts` uses (spec §8.4). */
export const MINUS = '−';

/** `1234567` → `1,234,567`. Sign is dropped; the caller adds it. */
export function groupDigits(n: number): string {
  const s = String(Math.abs(Math.trunc(n)));
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ',';
    out += s.charAt(i);
  }
  return out;
}

/** Agorot → whole shekels, half away from zero (so `−₪1,235` and `₪1,235` round symmetrically). */
export function toWholeShekels(agorot: number): number {
  const n = Number.isFinite(agorot) ? agorot : 0;
  return n < 0 ? -Math.round(-n / 100) : Math.round(n / 100);
}

/**
 * `₪1,234` · `−₪1,234` (REDESIGN §3: "Round to whole shekels … digits with thousands separators").
 * The result is one LTR token: the web wraps it in `dir="ltr"`.
 */
export function shekels(agorot: number): string {
  const whole = toWholeShekels(agorot);
  return `${whole < 0 ? MINUS : ''}₪${groupDigits(whole)}`;
}

/** Absolute magnitude in shekels — for the templates that already write `|net|`. */
export function shekelsAbs(agorot: number): string {
  return shekels(Math.abs(agorot));
}

/** `2026-09-12` → `12.09` (REDESIGN §3: "Dates `dd.mm`"). */
export function ddmm(day: DayString): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

/** `85%`, whole percent, one LTR token. */
export function percent(value: number): string {
  return `${Math.round(value)}%`;
}

/** `1.9` / `2` — one decimal, a trailing `.0` stripped so "פי 2" never reads "פי 2.0". */
export function ratio(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** `3` — plain integer, one LTR token. */
export function count(n: number): string {
  return groupDigits(Math.trunc(n));
}

/**
 * The forward countdown, grammatical in Hebrew: `היום` · `מחר` · `בעוד יומיים` · `בעוד 4 ימים`.
 * REDESIGN §2/§3 write the general form `בעוד {n} ימים`; 0/1/2 take the Hebrew forms because
 * "בעוד 1 ימים" is not Hebrew. `n` must be ≥ 0.
 */
export function inDaysPhrase(n: number): string {
  if (n <= 0) return 'היום';
  if (n === 1) return 'מחר';
  if (n === 2) return 'בעוד יומיים';
  return `בעוד ${count(n)} ימים`;
}

/** The overdue countdown: `באיחור יום` · `באיחור יומיים` · `באיחור 3 ימים`. `k` is ≥ 1. */
export function latePhrase(k: number): string {
  if (k <= 1) return 'באיחור יום';
  if (k === 2) return 'באיחור יומיים';
  return `באיחור ${count(k)} ימים`;
}

/**
 * The countdown chip of an urgency row (REDESIGN §4.3 "the date with a countdown chip
 * `בעוד 4 ימים` / `באיחור 3 ימים`"). Negative `daysUntil` is late.
 */
export function countdownLabel(daysUntil: number): string {
  return daysUntil < 0 ? latePhrase(-daysUntil) : inDaysPhrase(daysUntil);
}

/**
 * The backward phrase for a NON-deadline kind whose date is in the past: `אתמול` · `לפני יומיים` ·
 * `לפני 5 ימים`. A returned standing order or a purchase that already happened is history, not
 * lateness — `latePhrase` would claim `באיחור` for something nobody owes (`urgency.ts` `isLate`
 * already refuses to mark these late). `k` is ≥ 1.
 */
export function agoPhrase(k: number): string {
  if (k <= 1) return 'אתמול';
  if (k === 2) return 'לפני יומיים';
  return `לפני ${count(k)} ימים`;
}

/**
 * The countdown chip for an item whose kind is NOT a deadline: forward dates read as usual, a past
 * date reads `לפני N ימים` instead of `באיחור N ימים`.
 */
export function pastLabel(daysUntil: number): string {
  return daysUntil < 0 ? agoPhrase(-daysUntil) : inDaysPhrase(daysUntil);
}
